(() => {
  'use strict';
  const configNode = document.getElementById('bb-wc-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const s = config.strings;
  const scope = '.wishlist_club_width, .wcWishlistModal, .bb-wc-product, .wcAlertBox';
  const productCache = new Map();
  const cardState = new WeakMap();
  const modalState = new WeakSet();
  let scheduled = false;
  let lastTrigger;
  let stockLinkOpened = false;
  let formCounter = 0;

  const normalize = value => value.trim().replace(/\s+/g, ' ').toLowerCase().replace(/[.!]$/, '');
  const translations = new Map();
  const words = (key, ...values) => values.forEach(value => translations.set(normalize(value), s[key]));
  words('favorite', 'Add To Wishlist', 'Add to wish list', 'Save to wishlist');
  words('favorited', 'Added to wishlist', 'Saved to wishlist');
  words('removeFavorite', 'Remove Wishlist', 'Remove from wishlist');
  words('favorites', 'My wishlist', 'My wishlists', 'Wishlist');
  words('savedForLater', 'Save for later', 'Saved for later');
  words('backInStock', 'Back In Stock', 'Notify me', 'Notify me when available', 'Notify me when back in stock');
  words('notifySubmit', 'Create a New Back in Stock', 'Notify me when it is available', 'Subscribe');
  words('chooseVariant', 'Select Variant', 'Select a variant', 'Choose variant');
  words('close', 'Close Wishlist Modal', 'Close modal', 'Close');
  words('add', 'Add to cart');
  words('soldOut', 'Sold out', 'Out of stock');
  words('updateList', 'Update List', 'Update wishlist');
  words('email', 'Your email', 'Email address', 'Enter your email', 'Enter email address', 'Email');
  words('invalidEmail', 'Please enter a valid email address', 'Please enter valid email', 'Invalid email address');
  words('verifyEmail', 'We have sent verification mail to your email. Please verify your email.', 'We have sent verification email to your email. Please verify your email.');
  words('subscribed', 'You already have subscribed for this item', 'You have already subscribed for this item');
  words('willNotify', 'We will notify you when the item is available', 'You have successfully subscribed');
  words('addedFavorite', 'Product added to wishlist', 'Item added to wishlist');
  words('removedFavorite', 'Product removed from wishlist', 'Item removed from wishlist');
  words('wishlistUpdated', 'Wishlist updated successfully', 'List updated successfully');

  function translate(value) {
    const exact = translations.get(normalize(value));
    if (exact) return exact;
    const remove = value.match(/^Remove the wishlist item\s+(.+)$/i);
    if (remove) return `${s.removeFavorite}: ${remove[1]}`;
    if (/ from wishlist$/i.test(value)) {
      const label = value.replace(/ from wishlist$/i, '');
      return label === 'save-for-later' ? s.savedForLater : label;
    }
    return value;
  }

  function translateUI(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: node => node.parentElement.closest('script, style, option, .wcPcContent h4, .bb-wc-price, .bb-wc-actions') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(node => {
      if (!node.textContent.trim()) return;
      const value = translate(node.textContent.trim());
      if (value !== node.textContent.trim()) node.textContent = value;
    });
    root.querySelectorAll('[aria-label], [placeholder], [title]').forEach(element => {
      if (element.closest('.wcPcContent h4, .bb-wc-actions')) return;
      ['aria-label', 'placeholder', 'title'].forEach(attribute => {
        const value = element.getAttribute(attribute);
        if (value && translate(value) !== value) element.setAttribute(attribute, translate(value));
      });
    });
    root.querySelectorAll('select').forEach(select => {
      const onlyDefault = select.options.length === 1 && normalize(select.options[0].textContent) === 'default title';
      if (select.hasAttribute('data-bb-default-variant') !== onlyDefault) select.toggleAttribute('data-bb-default-variant', onlyDefault);
    });
    root.querySelectorAll('.wcPcContent span, .wcPcContent p').forEach(element => {
      if (!element.children.length && normalize(element.textContent) === 'default title' && !element.hasAttribute('data-bb-default-title')) element.setAttribute('data-bb-default-title', '');
    });
    root.querySelectorAll('input[name="bis_email"]').forEach(input => {
      if (input.type !== 'email') input.type = 'email';
      input.autocomplete = 'email';
      input.inputMode = 'email';
    });
  }

  function productURL(card) {
    const href = card.querySelector('a[href*="/products/"]')?.getAttribute('href') || (card.closest('.wcBisInfo') ? config.productPath : null);
    if (!href) return null;
    const url = new URL(href, location.origin);
    return url.origin === location.origin && /\/products\/[^/]+\/?$/.test(url.pathname) ? url : null;
  }

  function selectedID(card, url) {
    const select = card.closest('.wcBisInfo')?.querySelector('select[name="variants"]') || card.querySelector('select[name="variants"]');
    const value = select?.value || url.searchParams.get('variant') || '';
    return value.match(/(?:^|\/)(\d+)$/)?.[1] || '';
  }

  function productData(url) {
    const path = `${url.pathname.replace(/\/$/, '')}.js`;
    if (!productCache.has(path)) productCache.set(path, fetch(path, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(response => {
      if (!response.ok) throw new Error('Product unavailable');
      return response.json();
    }).then(product => {
      if (!Array.isArray(product.variants)) throw new Error('Invalid product');
      return product;
    }));
    return productCache.get(path);
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function renderPrice(card, variant) {
    const payments = window.BlocoboxPayments;
    const native = card.querySelector('.wcPcPrice');
    if (!payments || !native || !Number.isFinite(variant.price)) return;
    const price = element('div', 'bb-wc-price');
    if (variant.compare_at_price > variant.price) price.append(element('s', 'bb-wc-price__compare', payments.money(variant.compare_at_price)));
    const main = element('div', 'bb-wc-price__main');
    main.append(element('strong', '', payments.money(Math.round(variant.price * (100 - payments.pixPercent) / 100))));
    if (payments.pixPercent > 0) main.append(element('span', 'bb-wc-price__label', s.pix));
    price.append(main);
    const terms = payments.terms(variant.price, true);
    if (terms) price.append(element('div', 'bb-wc-price__terms', terms));
    const old = native.parentElement.querySelector('.bb-wc-price');
    if (old) old.replaceWith(price); else native.after(price);
    native.setAttribute('data-bb-native-price', '');
  }

  function replaceActions(card, action) {
    const footer = card.querySelector('.wcPcFooter');
    if (!footer) return;
    const previous = footer.querySelector('.bb-wc-actions');
    if (previous) previous.replaceWith(action); else footer.append(action);
    if (!card.classList.contains('bb-wc-card-ready')) card.classList.add('bb-wc-card-ready');
  }

  function productLink(card, url, label, notify = false) {
    const wrapper = element('div', 'bb-wc-actions');
    const link = element('a', 'bb-wc-add', label);
    const destination = new URL(url);
    if (notify) destination.hash = 'bb-avise-me';
    link.href = destination.href;
    wrapper.append(link);
    replaceActions(card, wrapper);
  }

  async function renderCartAction(card, url, product, variant, state) {
    if (!variant || product.requires_selling_plan) {
      productLink(card, url, s.options);
      return;
    }
    if (!variant.available) {
      productLink(card, url, s.backInStock, true);
      return;
    }
    // Reuse the theme ProductForm: stock validation, bundled sections, cart errors and drawer.
    // The app's paid redirect/callback option and private endpoints are not used.
    await customElements.whenDefined('product-form');
    if (!card.isConnected || cardState.get(card) !== state) return;
    const form = document.createElement('form', { is: 'product-form' });
    form.className = 'bb-wc-actions';
    form.setAttribute('id', `bb-wc-cart-${++formCounter}`);
    form.method = 'post';
    form.action = `${config.root.replace(/\/$/, '')}/cart/add`;
    [['id', variant.id], ['quantity', 1]].forEach(([name, value]) => {
      const input = element('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.append(input);
    });
    const button = element('button', 'bb-wc-add', s.add);
    button.type = 'submit';
    const error = element('p', 'bb-wc-error');
    error.setAttribute('role', 'alert');
    error.hidden = true;
    form.append(button, error);
    form.addEventListener('submit', () => {
      error.hidden = true;
      const quantity = Number(card.querySelector('input[name="quantity"]:not([type="hidden"])')?.value || 1);
      form.elements.namedItem('quantity').value = Number.isInteger(quantity) && quantity > 0 ? quantity : 1;
    }, true);
    form.addEventListener('cart:error', event => {
      error.textContent = event.detail.error;
      error.hidden = false;
    });
    replaceActions(card, form);
  }

  async function enhanceCard(card) {
    const url = productURL(card);
    if (!url) return;
    const id = selectedID(card, url);
    const key = `${url.pathname}|${id}`;
    const inList = Boolean(card.closest('.wishlist_club_width'));
    const previous = cardState.get(card);
    if (previous?.key === key && (previous.pending || previous.failed || (previous.complete && (!card.querySelector('.wcPcPrice') || card.querySelector('.bb-wc-price')) && (!inList || card.querySelector('.bb-wc-actions'))))) return;
    const state = { key, pending: true };
    cardState.set(card, state);
    if (inList) productLink(card, url, s.viewProduct);
    try {
      const product = await productData(url);
      if (!card.isConnected || cardState.get(card) !== state) return;
      const variant = id ? product.variants.find(item => String(item.id) === id) : product.variants.length === 1 ? product.variants[0] : null;
      state.variant = variant?.available && !product.requires_selling_plan ? variant : null;
      if (variant) renderPrice(card, variant);
      if (inList) await renderCartAction(card, url, product, variant, state);
      state.pending = false;
      state.complete = true;
      // A card without a selected real variant must link to the product, never add a guessed option.
      if (!variant) state.failed = true;
    } catch {
      state.pending = false;
      state.failed = true; // Keep the native price and safe product link if the public product API fails.
    } finally {
      schedule();
    }
  }

  function availableItems(root) {
    return [...root.querySelectorAll('.wcProductCard')].flatMap(card => {
      const variant = cardState.get(card)?.variant;
      if (!variant) return [];
      const quantity = Number(card.querySelector('input[name="quantity"]:not([type="hidden"])')?.value || 1);
      return [{ id: variant.id, quantity: Number.isInteger(quantity) && quantity > 0 ? quantity : 1 }];
    });
  }

  function enhanceBulk(root) {
    const native = root.querySelector('.wc_allAddtocartBtn');
    if (!native) return;
    let wrapper = native.querySelector('.bb-wc-actions');
    if (!wrapper) {
      wrapper = element('div', 'bb-wc-actions');
      const button = element('button', 'bb-wc-add');
      button.type = 'button';
      const error = element('p', 'bb-wc-error');
      error.setAttribute('role', 'alert');
      error.hidden = true;
      wrapper.append(button, error);
      native.append(wrapper);
      native.classList.add('bb-wc-bulk-ready');
      button.addEventListener('click', async () => {
        if (button.disabled) return;
        const items = availableItems(root);
        if (!items.length) return;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        error.hidden = true;
        const base = `${config.root.replace(/\/$/, '')}/`;
        let feedback = s.cartError;
        try {
          const sections = ['variant-added'];
          document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
          // Add to the existing cart. No clearing, checkout redirect or WC paid callback.
          const response = await fetch(`${base}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items, sections: [...new Set(sections)].slice(0, 5), sections_url: location.pathname }) });
          const added = await response.json();
          if (!response.ok) {
            feedback = added.description || s.cartError;
            throw new Error(feedback);
          }
          const cartResponse = await fetch(`${base}cart.js`, { credentials: 'same-origin' });
          if (!cartResponse.ok) throw new Error(s.cartError);
          const cart = await cartResponse.json();
          if (!Array.isArray(cart.items)) throw new Error(s.cartError);
          cart.sections = added.sections;
          button.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: added.items || [], cart } }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
        } catch {
          error.textContent = feedback;
          error.hidden = false;
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } finally {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      });
    }
    const count = availableItems(root).length;
    const pending = [...root.querySelectorAll('.wcProductCard')].some(card => cardState.get(card)?.pending);
    wrapper.hidden = count === 0 || pending;
    const button = wrapper.querySelector('button');
    const label = s.addAvailable.replace('[count]', count);
    if (button.textContent !== label) button.textContent = label;
  }

  function enhanceModal(modal) {
    if (!modal.getClientRects().length) return;
    if (modal.getAttribute('aria-hidden') !== 'false') modal.setAttribute('aria-hidden', 'false');
    if (modalState.has(modal)) return;
    modalState.add(modal);
    const heading = modal.querySelector('h3');
    if (heading) {
      heading.id ||= `bb-wc-dialog-${++formCounter}`;
      modal.setAttribute('aria-labelledby', heading.id);
    }
    requestAnimationFrame(() => modal.querySelector('.wcWishlistModal_close')?.focus({ preventScroll: true }));
  }

  function scan() {
    scheduled = false;
    document.querySelectorAll(scope).forEach(root => {
      translateUI(root);
      root.querySelectorAll('.wcProductCard').forEach(enhanceCard);
      if (root.matches('.wcWishlistModal')) enhanceModal(root);
      if (root.matches('.wishlist_club_width')) enhanceBulk(root);
    });
    if (!stockLinkOpened && location.hash === '#bb-avise-me') {
      const trigger = document.querySelector('.bb-wc-product .bis_product');
      if (trigger && trigger.getClientRects().length) {
        stockLinkOpened = true;
        trigger.click();
      }
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(scan);
  }

  const observer = new MutationObserver(mutations => {
    if (mutations.some(mutation => {
      const target = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
      return target?.closest(scope) || [...mutation.addedNodes].some(node => node.nodeType === Node.ELEMENT_NODE && (node.matches(scope) || node.querySelector(scope)));
    })) schedule();
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'aria-label', 'href', 'value'] });
  document.addEventListener('change', event => {
    if (event.target.closest(scope)) {
      const root = event.target.closest('.wcBisInfo') || event.target.closest('.wcProductCard');
      const card = root?.matches('.wcProductCard') ? root : root?.querySelector('.wcProductCard');
      if (card) cardState.delete(card);
      schedule();
    }
  });
  document.addEventListener('click', event => {
    if (event.target.closest('.bb-wc-product button, .wishlist_club_width button')) lastTrigger = event.target.closest('button');
    if (event.target.closest('.wcWishlistModal_close')) requestAnimationFrame(() => lastTrigger?.isConnected && lastTrigger.focus({ preventScroll: true }));
  }, true);
  document.addEventListener('keydown', event => {
    const modal = document.querySelector('.wcWishlistModal[aria-hidden="false"]');
    if (!modal?.getClientRects().length) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      modal.querySelector('.wcWishlistModal_close')?.click();
    }
    if (event.key !== 'Tab') return;
    const focusable = [...modal.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]')].filter(node => node.getClientRects().length);
    const first = focusable[0], last = focusable.at(-1);
    if (first && ((!event.shiftKey && document.activeElement === last) || (event.shiftKey && document.activeElement === first) || !modal.contains(document.activeElement))) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  });
  window.addEventListener('pageshow', schedule);
  document.addEventListener('shopify:section:load', schedule);
  scan();
})();
